// Goal Detail — docs/screens/07-goal-detail.md.
//
// Goal = milestone do GitHub em by-mav/hope. Gasto e teto vivem no team
// LiteLLM `goal-{n}`; sessão é chave etiquetada do goal, lida uma a uma em
// /spend/logs?api_key=; DoD e progress log são arquivos em goals/<slug>/.
// Elementos que o contrato marca "sem dono" ou "INFERIDO — runtime" sem
// lugar de leitura (Nudge, Pause, Restart session, badge Runner online,
// Stop por sessão, log de decisões, campos Stuck/Session budget/Grace/
// Execution) ficam fora — nada de botão morto.

import {
  empty, errorBox, esc, head, md, money, pill, skeleton, table, tabs, toast,
} from "../ui";
import { gateway, github, litellm, SourceError, type SpendLog } from "../api";

const REPO = "by-mav/hope";

const CSS = `
.goal-badges{display:inline-flex;align-items:center;gap:8px;margin-right:6px}
.goal-danger{background:var(--crit);border-color:var(--crit);color:#fff}
.goal-danger:hover{background:#c9524a;border-color:#c9524a}
.goal-desc{position:relative;max-height:7.6em;overflow:hidden;margin-bottom:4px}
.goal-desc[data-open]{max-height:none}
.goal-desc:not([data-open])::after{content:"";position:absolute;inset:auto 0 0 0;height:2.2em;background:linear-gradient(transparent,var(--bg))}
.goal-more{display:inline-block;font-size:12.5px;color:var(--accent);text-decoration:none;margin-bottom:10px}
.goal-origin{display:flex;align-items:center;gap:7px;font-size:12.5px;color:var(--ink-3);margin-bottom:14px}
.goal-banner{display:flex;align-items:center;gap:10px;background:var(--surface);border:1px solid var(--rule);border-radius:var(--r);padding:10px 14px;font-family:var(--mono);font-size:12.5px;margin-bottom:14px}
.goal-spin{width:12px;height:12px;border:2px solid var(--rule-2);border-top-color:var(--accent);border-radius:50%;animation:goal-spin .8s linear infinite}
@keyframes goal-spin{to{transform:rotate(360deg)}}
.goal-cards{grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:16px}
.goal-cards .metric-value{font-size:15px;line-height:1.5}
.goal-tab{margin-top:16px}
.goal-modal-back{position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;z-index:50}
.goal-modal{width:min(440px,92vw);max-height:88vh;overflow:auto}
.goal-modal-head{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-bottom:1px solid var(--rule)}
.goal-modal-foot{display:flex;justify-content:flex-end;gap:8px;margin-top:6px}
`;

if (!document.getElementById("css-goal-detail")) {
  const st = document.createElement("style");
  st.id = "css-goal-detail";
  st.textContent = CSS;
  document.head.append(st);
}

// ------------------------------------------------------------------- formas

interface Milestone {
  number: number;
  title: string;
  description: string | null;
  state: "open" | "closed";
}

interface IssueDoD {
  state: "open" | "closed";
  state_reason?: string | null;
  pull_request?: unknown;
}

interface KeyInfo {
  token?: string;
  key_alias?: string | null;
  models?: string[];
}

interface TeamInfoResp {
  spend?: number;
  max_budget?: number | null;
  team_info?: { spend?: number; max_budget?: number | null };
}

// ---------------------------------------------------------------- derivados

/** Slug do goal a partir do título do milestone — convenção goals/<slug>/.
 *  Corta nas primeiras palavras: o objetivo é uma frase inteira, e o nome do
 *  spec aparece na tela ("farming.md" no app de referência). Título grande
 *  vira caminho grande e ilegível. */
const slugDe = (titulo: string): string =>
  titulo
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .split("-")
    .slice(0, 4)
    .join("-");

const metaStr = (l: SpendLog, campo: string): string => {
  const v = l.metadata?.[campo];
  return typeof v === "string" ? v : "";
};

/** Agente da sessão, do metadata da chave etiquetada (quando gravado lá). */
function agenteDe(l: SpendLog): string {
  const bruto = l.metadata?.["user_api_key_metadata"];
  let obj: unknown = bruto;
  if (typeof bruto === "string") {
    try { obj = JSON.parse(bruto); } catch { obj = null; }
  }
  const a = (obj as Record<string, unknown> | null)?.["agent"];
  return typeof a === "string" ? a : "";
}

/** "Vivo" = entrada nos últimos 15 min. `running` de verdade só o runner
 *  sabe (INFERIDO — runtime); isto é a base derivável do contrato.
 *  ponytail: janela fixa; se o runner publicar estado um dia, ler de lá. */
const recente = (iso: string): boolean =>
  !!iso && Date.now() - new Date(iso).getTime() < 15 * 60_000;

interface Sessao {
  alias: string;
  agente: string;
  modelo: string;
  inicio: string;
  atividade: string;
  custoCents: number;
}

/** Chaves do goal. A sessão é a chave efêmera etiquetada `goal-{n}`, então a
 *  lista de chaves — não o log de gasto — é quem diz quais sessões existem.
 *  Com team, o próprio /key/list filtra; sem team, casa pelo alias. */
async function chavesDoGoal(n: number, teamId: string | undefined): Promise<KeyInfo[]> {
  const q = teamId ? `?team_id=${teamId}&return_full_object=true` : "?return_full_object=true";
  const r = await gateway<{ keys?: KeyInfo[] }>(`litellm/key/list${q}`).catch(() => ({
    keys: [] as KeyInfo[],
  }));
  return (r.keys ?? []).filter((k) => teamId || (k.key_alias ?? "").includes(`goal-${n}`));
}

/** Uma sessão por chave, montada com `GET /spend/logs?api_key=` — poucas
 *  respostas pequenas, uma por chave do goal. O log cru sem `api_key` traz o
 *  histórico inteiro do gateway (81 MB) só para agrupar de volta por chave. */
async function sessoesDoGoal(chaves: KeyInfo[]): Promise<Sessao[]> {
  const comToken = chaves.filter((k): k is KeyInfo & { token: string } => !!k.token);
  const logs = await Promise.all(
    comToken.map((k) => litellm.spendForKey(k.token).catch(() => [] as SpendLog[])),
  );
  const sessoes = comToken
    .map((k, i) => {
      const ls = [...logs[i]].sort((a, b) => (a.startTime ?? "").localeCompare(b.startTime ?? ""));
      const ultimo = ls[ls.length - 1];
      return {
        alias: k.key_alias || (ultimo ? metaStr(ultimo, "user_api_key_alias") : "") || k.token.slice(0, 12),
        agente: ultimo ? agenteDe(ultimo) : "",
        modelo: ultimo?.model ?? "",
        inicio: ls[0]?.startTime ?? "",
        // O log cru não expõe `endTime`: a última chamada da chave é o sinal de
        // atividade mais recente que o gateway dá.
        atividade: ultimo?.startTime ?? "",
        custoCents: Math.round(ls.reduce((s, l) => s + (l.spend ?? 0), 0) * 100),
      };
    })
    // Chave criada e nunca usada não é sessão — não conta como iteração.
    .filter((s) => !!s.inicio);
  sessoes.sort((a, b) => a.inicio.localeCompare(b.inicio));
  return sessoes;
}

// ------------------------------------------------------------------- blocos

const cardMetrica = (rotulo: string, valor: string): string =>
  `<div class="card metric">
     <div class="metric-label">${esc(rotulo)}</div>
     <div class="metric-value">${esc(valor)}</div>
   </div>`;

/** Documento markdown do repo (dod.md, progress.md). `null` = não existe. */
async function arquivo(path: string): Promise<string | null> {
  try {
    const r = await gateway<{ content?: string }>(`github/repos/${REPO}/contents/${path}`);
    if (!r.content) return null;
    const bytes = Uint8Array.from(atob(r.content.replace(/\n/g, "")), (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

async function abaArquivo(path: string, vazioTitulo: string, vazioCorpo: string): Promise<string> {
  const texto = await arquivo(path);
  return texto
    ? `<div class="card"><div class="card-body">${md(texto)}</div></div>`
    : empty(vazioTitulo, vazioCorpo);
}

/** Tabela de sessões — serve às abas Sessions e Orchestrator (mesma fonte;
 *  o conteúdo da aba Sessions nunca foi visto e é INFERIDO no spec). */
function tabelaSessoes(sessoes: Sessao[]): string {
  if (!sessoes.length) {
    return empty(
      "Nenhuma sessão registrada",
      "As sessões aparecem aqui assim que o orquestrador despachar a primeira iteração deste goal.",
    );
  }
  return table(
    ["#", "Session", "Agent", "State", "Cost"],
    sessoes.map((s, i) => {
      const rodando = recente(s.atividade);
      return `<tr>
        <td class="num">${i + 1}</td>
        <td class="mono">${esc(s.alias || "—")}</td>
        <td>${esc(s.agente || "—")}</td>
        <td>${pill(rodando ? "running" : "done", rodando ? "run" : "ok")}${s.modelo ? " " + pill(s.modelo, "idle") : ""}</td>
        <td class="num">${money(s.custoCents)}</td>
      </tr>`;
    }),
  );
}

/** Aba Results: só a tabela de arquivos tem dono (conteúdo do repo). O
 *  relatório da sessão não tem endpoint HTTP — fica fora até o runner
 *  publicar o transcript no repo (inbox #8). */
async function abaResults(slug: string): Promise<string> {
  const vazio = empty(
    "Nenhum resultado ainda",
    `Os artefatos aparecem aqui quando as sessões gravarem arquivos em goals/${slug}/ no repo.`,
  );
  try {
    const arquivos = (await github.contents(REPO, `goals/${slug}`)).filter((a) => a.type === "file");
    if (!arquivos.length) return vazio;
    const datas = await Promise.all(
      arquivos.map((a) =>
        github.commits(REPO, a.path).then((cs) => cs[0]?.commit.committer.date ?? "").catch(() => ""),
      ),
    );
    return table(
      ["File", "Size", "Updated"],
      arquivos.map(
        (a, i) => `<tr>
          <td class="mono"><a href="${esc(a.html_url)}" target="_blank" rel="noopener">${esc(a.name)}</a></td>
          <td class="num">${(a.size / 1024).toFixed(1)} KB</td>
          <td>${datas[i] ? new Date(datas[i]).toLocaleString("pt-BR") : "—"}</td>
        </tr>`,
      ),
    );
  } catch {
    return vazio;
  }
}

// -------------------------------------------------------------------- modal

/** Adjust limits. Só os campos com dono nativo: Spend cap → max_budget do
 *  team; Wall-clock → duration da chave; runtimes → models da chave. Stuck
 *  threshold, Session budget, Grace period e Execution não têm dono (lista
 *  do contrato) e ficam fora até a inbox decidir. */
async function abreModal(el: HTMLElement, n: number, teamId: string | undefined): Promise<void> {
  const back = document.createElement("div");
  back.className = "goal-modal-back";
  back.innerHTML = `<div class="goal-modal card"><div class="card-body">${skeleton(3)}</div></div>`;
  document.body.append(back);
  const fecha = () => back.remove();
  back.addEventListener("click", (e) => {
    if (e.target === back) fecha();
  });

  let gasto: number | null = null;
  let teto: number | null = null;
  if (teamId) {
    try {
      const i = await gateway<TeamInfoResp>(`litellm/team/info?team_id=${teamId}`);
      gasto = i.team_info?.spend ?? i.spend ?? null;
      teto = i.team_info?.max_budget ?? i.max_budget ?? null;
    } catch {
      /* o campo mostra o que tem */
    }
  }
  const [chaves, modelos] = await Promise.all([
    chavesDoGoal(n, teamId),
    litellm.models().catch(() => [] as Array<{ id: string }>),
  ]);
  // ponytail: primeira chave do team = chave do goal; se o goal ganhar mais
  // de uma chave viva, escolher pela etiqueta da iteração corrente.
  const chave = chaves[0];

  const campoSpend = teamId
    ? `<div class="field">
         <label class="label" for="gl-cap">Spend cap (USD)</label>
         <input class="input" id="gl-cap" name="cap" type="number" min="0" step="1" value="${teto ?? ""}" />
         <span class="hint">${gasto != null ? esc(money(Math.round(gasto * 100))) + " spent so far." : "Gasto atual indisponível."}</span>
       </div>`
    : "";

  const opcoes = (sel: string | undefined) =>
    modelos
      .map((m) => `<option value="${esc(m.id)}" ${m.id === sel ? "selected" : ""}>${esc(m.id)}</option>`)
      .join("");

  // Wall-clock só depende da chave do goal (duration em POST /key/update); a
  // lista de modelos do gateway não é pré-requisito dele.
  const campoWall = chave
    ? `<div class="field">
         <label class="label" for="gl-wall">Wall-clock limit (hours)</label>
         <input class="input" id="gl-wall" name="wall" type="number" min="1" step="1" placeholder="72" />
         <span class="hint">Corta a chave do goal após N horas (duration no gateway). Vazio mantém o atual.</span>
       </div>`
    : "";

  // ponytail: convenção posicional models[0]=plan, models[1]=worker — não vem
  // do contrato (preferência plan/worker é "sem dono", inbox #8); models é
  // allowlist não-ordenada, migrar para o dono real quando a inbox decidir.
  const camposRuntime = chave && modelos.length
    ? `<div class="grid grid-2">
         <div class="field">
           <label class="label" for="gl-plan">Plan agent runtime</label>
           <select class="select" id="gl-plan" name="plan">${opcoes(chave.models?.[0])}</select>
         </div>
         <div class="field">
           <label class="label" for="gl-work">Worker runtime</label>
           <select class="select" id="gl-work" name="work">${opcoes(chave.models?.[1] ?? chave.models?.[0])}</select>
         </div>
       </div>`
    : "";

  back.querySelector<HTMLElement>(".goal-modal")!.innerHTML = `
    <div class="goal-modal-head">
      <span class="card-title">Adjust limits</span>
      <button class="btn btn-ghost" type="button" data-x aria-label="Fechar">×</button>
    </div>
    <div class="card-body">
      <form id="gl-form">
        ${campoSpend}${campoWall}${camposRuntime}
        ${
          campoSpend || campoWall || camposRuntime
            ? ""
            : `<p class="hint">Nenhum limite deste goal tem dono nativo ainda — crie o team goal-${n} no LiteLLM para habilitar o spend cap.</p>`
        }
        <div class="goal-modal-foot">
          <button class="btn" type="button" data-x>Cancel</button>
          <button class="btn btn-primary" type="submit" ${campoSpend || campoWall || camposRuntime ? "" : "disabled"}>Save limits</button>
        </div>
      </form>
    </div>`;

  back.querySelectorAll("[data-x]").forEach((b) => b.addEventListener("click", fecha));

  // Foco inicial do diálogo: primeiro campo com o valor selecionado (f114).
  back.querySelector<HTMLInputElement>(".goal-modal input")?.select();

  back.querySelector<HTMLFormElement>("#gl-form")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const botao = form.querySelector<HTMLButtonElement>('[type="submit"]')!;
    botao.disabled = true;
    const d = new FormData(form);
    try {
      if (teamId && d.get("cap")) {
        await gateway("litellm/team/update", {
          method: "POST",
          body: JSON.stringify({ team_id: teamId, max_budget: Number(d.get("cap")) }),
        });
      }
      if (chave?.token) {
        const corpo: Record<string, unknown> = { key: chave.token };
        if (d.get("wall")) corpo.duration = `${Number(d.get("wall"))}h`;
        const runtimes = [...new Set([d.get("plan"), d.get("work")].filter(Boolean).map(String))];
        if (runtimes.length) corpo.models = runtimes;
        if (corpo.duration || corpo.models) {
          await gateway("litellm/key/update", { method: "POST", body: JSON.stringify(corpo) });
        }
      }
      toast("Limites salvos");
      fecha();
      goalDetail(el);
    } catch (err) {
      toast((err as SourceError).message);
      botao.disabled = false;
    }
  });
}

// ------------------------------------------------------------------- página

const ABAS = [
  ["dod", "Definition of Done"],
  ["progress", "Progress log"],
  ["sessions", "Sessions"],
  ["orchestrator", "Orchestrator"],
  ["results", "Results"],
] as const;

export const goalDetail = async (el: HTMLElement): Promise<void> => {
  const partes = location.hash.replace(/^#\/?/, "").split("/");
  const n = Number(partes[1]);
  const aba = ABAS.some(([id]) => id === partes[2]) ? partes[2] : "dod";

  if (!Number.isFinite(n) || n <= 0) {
    el.innerHTML = head({ title: "Goal" }) +
      errorBox("hope", "goal sem número na rota", "abra um goal pela lista em #/goals");
    return;
  }

  el.innerHTML = head({ title: `Goal #${n}` }) + skeleton();

  try {
    const ms = await gateway<Milestone>(`github/repos/${REPO}/milestones/${n}`);
    const slug = slugDe(ms.title);

    type Times = Awaited<ReturnType<typeof litellm.teams>>;
    const [issues, times] = await Promise.all([
      gateway<IssueDoD[]>(`github/repos/${REPO}/issues?milestone=${n}&state=all&per_page=100`)
        .catch(() => [] as IssueDoD[]),
      litellm.teams().catch(() => [] as Times),
    ]);

    // DoD: item = issue do milestone. Derivação exata do contrato.
    const dod = issues.filter((i) => !i.pull_request);
    const feitos = dod.filter((i) => i.state === "closed" && i.state_reason === "completed").length;
    const dispensados = dod.filter((i) => i.state === "closed" && i.state_reason === "not_planned").length;
    const abertos = dod.filter((i) => i.state === "open").length;

    const time = times.find((t) => t.team_alias === `goal-${n}`);

    // Gasto e teto continuam vindo do team: o LiteLLM já mantém a soma, e o
    // card não precisa de log nenhum para desenhá-la.
    const [infoTime, chaves] = await Promise.all([
      time?.team_id
        ? gateway<TeamInfoResp>(`litellm/team/info?team_id=${time.team_id}`).catch(() => null)
        : Promise.resolve(null),
      chavesDoGoal(n, time?.team_id),
    ]);
    const gasto = infoTime?.team_info?.spend ?? infoTime?.spend ?? null;
    const teto = infoTime?.team_info?.max_budget ?? time?.max_budget ?? null;

    const sessoes = await sessoesDoGoal(chaves);
    const ultima = sessoes[sessoes.length - 1];
    const iteracao = sessoes.length;
    const ativo = ultima?.agente ?? "";

    // Estado derivável: milestone aberto + entrada recente ≈ ativo.
    const rodando = ms.state === "open" && !!ultima && recente(ultima.atividade);
    const badge = ms.state === "closed" ? pill("closed", "idle") : rodando ? pill("running", "run") : pill("open", "wait");

    const header = head({
      title: ms.title,
      actions: `<span class="goal-badges">${badge}</span>
        <button class="btn" data-adjust>Adjust limits</button>
        <button class="btn goal-danger" data-cancel ${ms.state === "closed" ? "disabled" : ""}>Cancel goal</button>`,
    });

    const descricao = ms.description
      ? `<div class="goal-desc" data-desc>${md(ms.description)}</div>
         <a class="goal-more" href="#/goals/${n}" data-more>˅ Show more</a>`
      : "";

    // Rótulo no pixel é só o nome do spec (`farming.md`); o path completo
    // (goals/<slug>/spec.md) fica na leitura do conteúdo no repo.
    const origem = `<div class="goal-origin">▤ Started from spec: <code>${esc(slug)}.md</code></div>`;

    const banner = rodando && ativo
      ? `<div class="goal-banner"><span class="goal-spin"></span>${esc(ativo)} working — iteration ${iteracao}</div>`
      : "";

    const cards = `<div class="grid goal-cards">
      ${cardMetrica("DoD progress", `${feitos} done · ${dispensados} waived · ${abertos} open`)}
      ${cardMetrica("Spend", gasto != null || teto != null
        ? `${money(Math.round((gasto ?? 0) * 100))} / ${teto != null ? money(Math.round(teto * 100)) : "—"}`
        : "—")}
      ${cardMetrica("Iteration", iteracao ? String(iteracao) : "—")}
      ${cardMetrica("Active agent", ativo || "—")}
    </div>`;

    const barra = tabs(
      ABAS.map(([id, label]) => ({
        label,
        href: `#/goals/${n}${id === "dod" ? "" : "/" + id}`,
        ativa: id === aba,
      })),
    );

    let conteudo: string;
    if (aba === "progress") {
      conteudo = await abaArquivo(
        `goals/${slug}/progress.md`,
        "Progress log ainda não escrito",
        `Os agentes gravam goals/${slug}/progress.md conforme trabalham.`,
      );
    } else if (aba === "sessions" || aba === "orchestrator") {
      conteudo = tabelaSessoes(sessoes);
    } else if (aba === "results") {
      conteudo = await abaResults(slug);
    } else {
      conteudo = await abaArquivo(
        `goals/${slug}/dod.md`,
        "Definition of Done ainda não gerada",
        `Anexe um spec ao criar o goal — o sistema escreve goals/${slug}/dod.md a partir dele.`,
      );
    }

    el.innerHTML = header + descricao + origem + banner + cards + barra +
      `<div class="goal-tab">${conteudo}</div>`;

    // Show more — expansão local da descrição, sem dado extra.
    const more = el.querySelector<HTMLAnchorElement>("[data-more]");
    more?.addEventListener("click", (e) => {
      e.preventDefault();
      const aberto = el.querySelector<HTMLElement>("[data-desc]")!.toggleAttribute("data-open");
      more.textContent = aberto ? "˄ Show less" : "˅ Show more";
    });

    // Cancel goal — fecha o milestone (persistente). Derrubar a sessão viva
    // é do runner (INFERIDO — runtime), fora daqui.
    el.querySelector<HTMLButtonElement>("[data-cancel]")?.addEventListener("click", async (e) => {
      const botao = e.currentTarget as HTMLButtonElement;
      if (!confirm("Encerrar este goal? O milestone será fechado.")) return;
      botao.disabled = true;
      try {
        await gateway(`github/repos/${REPO}/milestones/${n}`, {
          method: "PATCH",
          body: JSON.stringify({ state: "closed" }),
        });
        toast("Goal encerrado");
        goalDetail(el);
      } catch (err) {
        toast((err as SourceError).message);
        botao.disabled = false;
      }
    });

    el.querySelector<HTMLButtonElement>("[data-adjust]")?.addEventListener("click", () => {
      abreModal(el, n, time?.team_id);
    });
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML = head({ title: `Goal #${n}` }) +
      errorBox(err.source ?? "origem", err.message, err.next ?? "tente de novo");
  }
};
